import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Block, Button, DefaultText, FlexRow, GeneralTooltip, TextAreaBox, TextBox } from '@guryou/html-components';
import { generateZoom, getLatestZoomMeeting, stateIsLoading, useEffectOnError, useEffectOnSuccess, zoomCheckIfConnected } from '@guryou/redux';

const LocationUrlWithZoom = ({ locationObject, fullWidth, disabled }) => {
  const { t } = useTranslation();
  const [clickedOnce, setClickedOnce] = useState(false);
  const dispatch = useDispatch();
  const zoomUrl = useSelector(state => state.zoom.generate);
  const latestMeeting = useSelector(state => state.zoom.latestZoomMeeting);
  const isConnected = useSelector(state => state.zoom.isConnected);
  const [error, setError] = useState(null);
  const [zoomLink, setZoomLink] = useState('');
  const [zoomNote, setZoomNote] = useState(null);

  const handleGenerateZoom = () => {
    setClickedOnce(true);
    dispatch(generateZoom());
  };

  const handleGetLatestMeetingUrl = () => {
    dispatch(getLatestZoomMeeting());
  };

  useEffect(() => {
    dispatch(zoomCheckIfConnected());
  }, [dispatch]);

  useEffectOnError(
    state => state.zoom.generate,
    () => {
      setError(t('ZOOM.errorZoom'));
    }
  );

  useEffectOnSuccess(
    state => state.zoom.generate,
    data => {
      if (data) {
        setZoomLink(data);
        setZoomNote('Zoom url');
      }
    }
  );

  useEffectOnSuccess(
    state => state.zoom.latestZoomMeeting,
    data => {
      if (data) {
        setZoomLink(data.joinUrl);
        setZoomNote('Zoom url');
      }
    }
  );

  // Was a blocking `if (stateIsLoading(...)) return <FullScreenSpinner />;` here —
  // checking Zoom's connection status is a background check that only affects these
  // two buttons, not something worth blocking the entire modal/page for. The buttons
  // themselves now account for the connection check still being in flight instead.
  const connectionLoading = stateIsLoading([isConnected]);

  return (
    <FlexRow xs={12}>
      <TextBox
        key={`${zoomLink}_key`}
        disabled={disabled}
        fullWidth={fullWidth}
        xs={fullWidth && 12}
        label="URL"
        name="url"
        value={zoomLink || locationObject.url || locationObject.locationNote || locationObject.note || ''}
      />
      <GeneralTooltip title={t('ZOOM.getLastExplanation')} aria-label="last meeting">
        <Block width="auto" marginLeft={2.8}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleGetLatestMeetingUrl}
            disabled={connectionLoading || isConnected?.data === false || stateIsLoading([latestMeeting])}
          >
            {t('ZOOM.getLastMeeting')}
          </Button>
        </Block>
      </GeneralTooltip>
      <GeneralTooltip title={t('ZOOM.createExplanation')} aria-label="generate meeting">
        <Block width="auto" marginLeft={2.8}>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={handleGenerateZoom}
            disabled={connectionLoading || clickedOnce || isConnected?.data === false || stateIsLoading([zoomUrl])}
          >
            {t('ZOOM.createMeeting')}
          </Button>
        </Block>
      </GeneralTooltip>

      {error && (
        <DefaultText xs={12} marginLeft={4} color="red">
          {error}
        </DefaultText>
      )}
      <TextAreaBox
        key={`${zoomNote}_key`}
        disabled={disabled}
        fullWidth={fullWidth}
        xs={fullWidth && 12}
        label={t('GENERAL.leaveNote')}
        placeholder={t('GENERAL.enterValidUrl')}
        name="locationNote"
        value={zoomNote || locationObject.locationNote || locationObject.note || ''}
      />
    </FlexRow>
  );
};

export default LocationUrlWithZoom;
